"use client";

import { useRef } from "react";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import gridBGimg from "@/public/lp2/dear/grid.svg";
import blurImg from "@/public/lp2/dear/blur.svg";
import trademaniaLogoImg from "@/public/trading-guide/logo.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function ExpectSection({ data }: { data: PageMentoringSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const dearTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      dearTL.from(content1Ref.current.children, {
        y: "1rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: content1Ref.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
  <div className="container-lp mx-auto pt-[10rem] lg:pt-[20rem] pb-[20rem]">
    <div className="flex flex-col items-start justify-center w-full">
      <div ref={content1Ref} className="w-full max-w-[180rem] mx-auto">
        <div className="mb-[6rem] lg:mb-[8rem] w-full relative">
          {/* grid */}
          <div className="w-full lg:w-1/2 absolute left-1/2 top-[0%] -translate-x-1/2 -translate-y-1/2 z-[-1]">
            <Image src={gridBGimg} alt="grid background" className="w-full" />
          </div>
          {/* shadow/blur */}
          <div className="w-full lg:w-1/2 absolute left-1/2 top-[0%] -translate-x-1/2 -translate-y-1/2 z-[-1]">
            <Image src={blurImg} alt="blur background" className="dear-section-blur w-full" />
          </div>

          {data?.expectTitle && (
            <PortableText
              value={data?.expectTitle ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title text-[4.5rem] lg:text-[7rem] font-[500] tracking-[-0.29rem] leading-[110%] w-full mb-[4rem] lg:mb-[6rem] text-center">
                      {children}
                    </h2>
                  ),
                },
              })}
            />
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-[4rem] relative z-[2]">
        {data?.expectCards?.map((card, index) => (
          <div
            key={index}
            className={
              index === 3
                ? "w-full h-full relative lg:col-span-2 pb-[.1rem] pr-[.1rem]"
                : "w-full h-full relative pb-[.1rem] pr-[.1rem]"
            }
          >
            {/* border */}
            <div className="absolute z-[-1] w-full h-full bg-black left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-[2rem]"></div>
            <div className="absolute z-[-2] w-[calc(100%+.125rem)] h-[calc(100%+.125rem)] bg-gradient-to-br from-[#80FFD9]/50 via-white/20 to-[#80FFD9]/20 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-[2rem]"></div>
            {/* end border */}
            <div className="h-full rounded-[2rem] backdrop-blur-[4rem] bg-gradient-to-br from-black/0 via-[#80FFD9]/5 to-[#80FFD9]/[.04] px-[4rem] py-[4.5rem]">
              <div className="h-full flex flex-col justify-between gap-[3rem]">
                <GroqImage
                  src={card.icon}
                  alt="icon"
                  className="!w-[6.3rem] !h-[6.3rem] !min-w-[6.3rem] !min-h-[6.3rem] mb-[1rem]"
                />
                <div>
                  <h4 className="gradient-title sans text-[3.5rem] lg:text-[4rem] font-[500] tracking-[-0.29rem] leading-[110%] mb-[2rem]">
                    {card.title}
                  </h4>
                  {card?.text && (
                    <PortableText
                      value={card.text ?? []}
                      components={customRichTextComponent({
                        block: {
                          normal: ({ children }) => (
                            <p className="sans text-white/80 text-[2.25rem] font-[400] leading-[140%]">
                              {children}
                            </p>
                          ),
                        },
                      })}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="w-full max-w-[90vw] lg:max-w-[140rem] relative z-[2] mx-auto mt-[10rem] lg:mt-[14rem]">
        {/* border */}
        <div className="absolute z-[-1] w-full h-full bg-black left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 lg:rounded-[10rem] rounded-[4rem]"></div>
        <div className="absolute z-[-2] w-[calc(100%+.125rem)] h-[calc(100%+.125rem)] bg-gradient-to-r from-[#61FFCE]/50 via-white/20 to-[#61FFCE]/5 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 lg:rounded-[10rem] rounded-[4rem]"></div>
        {/* end border */}
        <div className="lg:rounded-[10rem] rounded-[4rem] backdrop-blur-[4rem] bg-black/20 px-[4rem] pt-[5rem] pb-[3rem] lg:ps-[8rem] lg:pe-[6rem] lg:py-[6rem] flex flex-col lg:flex-row lg:justify-between items-center gap-4">
          <Image
            src={trademaniaLogoImg}
            alt="logo"
            className="absolute top-[-3rem] left-1/2 -translate-x-1/2 lg:translate-x-0 lg:left-[8rem] !w-[6rem] !h-[6rem] !min-w-[6rem] !min-h-[6rem]"
          />
          <div className="flex flex-col gap-1">
            {data?.expectCTATexts && (
              <PortableText
                value={data?.expectCTATexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h3: ({ children }) => (
                      <h3 className="gradient-title sans text-[3rem] lg:text-[3.5rem] font-[500] tracking-[-0.29rem] leading-[110%] lg:leading-[129%] mb-0">
                        {children}
                      </h3>
                    ),
                    normal: ({ children }) => (
                      <p className="sans text-white/80 text-[2rem] font-[400] leading-[140%]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}
          </div>

          {data?.expectCTA && <CTAButton data={data?.expectCTA} />}

          {/* blur */}
          <div className="absolute z-[-1] w-full h-full bg-[#1ECC97] opacity-20 left-1/2 -translate-x-1/2 top-1/2 rounded-[10rem] blur-[10.25rem]"></div>
          {/* end blur */}
        </div>
      </div>
    </div>
  </div>
</section>

  );
}
